'use client';
import { useMemo } from 'react';
import { useHoldingsData } from '@/lib/hooks/use-holdings-data';
import { compositeFor, compTier } from './HoldingsList.helpers';

/**
 * Per-holding composite trajectory at 1w / 1m / 3m / 6m / 1y lookbacks.
 *
 * Lifted out of the main HoldingsList table 2026-05-09 because the 5 history
 * cols squished the holdings display. Moved to its own /portfolio tab between
 * Scorecard and Earnings — narrow surface with breathing room, easy to scan.
 */
function renderDelta(curr: number | null | undefined, ago: number | null | undefined) {
  if (curr == null || ago == null || !Number.isFinite(curr) || !Number.isFinite(ago)) {
    return <span className="text-[#475569]">—</span>;
  }
  const d = curr - ago;
  const color = d > 0 ? 'text-emerald-400' : d < 0 ? 'text-red-400' : 'text-[#94a3b8]';
  const sign = d >= 0 ? '+' : '';
  return <span className={`tabular-nums ${color}`}>{`${sign}${d.toFixed(2)}`}</span>;
}

function renderHist(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return <span className="text-[#475569]">—</span>;
  const tier = compTier(value);
  return <span className={`tabular-nums font-semibold ${tier.color}`}>{value.toFixed(2)}</span>;
}

export function HoldingsHistory({ symbols: symbolsOverride, weights: weightsOverride, title }: { symbols?: string[]; weights?: Record<string, number>; title?: string } = {}) {
  const { portfolio, scores, historyBySymbol } = useHoldingsData();

  const rows = useMemo(() => {
    const syms = symbolsOverride ?? portfolio?.symbols;
    if (!syms || syms.length === 0) return [];
    const w = symbolsOverride
      ? (weightsOverride ?? Object.fromEntries(syms.map((s) => [s, 1 / Math.max(syms.length, 1)])))
      : (portfolio?.weights ?? {});
    const eq = 1 / Math.max(syms.length, 1);
    return syms
      .map((sym: string) => {
        const score = scores?.get(sym);
        const composite = compositeFor(score?.q ?? null, score?.v ?? null);
        const hist = historyBySymbol.get(sym) ?? { w1: null, m1: null, m3: null, m6: null, y1: null };
        return {
          symbol: sym,
          weight: w[sym] ?? eq,
          composite,
          hist,
        };
      })
      .sort((a, b) => b.weight - a.weight);
  }, [symbolsOverride, weightsOverride, portfolio, scores, historyBySymbol]);

  if (rows.length === 0) {
    return <div className="text-[#64748b] text-sm">No holdings to display.</div>;
  }

  return (
    <div className="border border-[#1e293b] rounded-lg bg-[#0d1117]/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-[#1e293b]">
        <div className="text-amber-400 text-sm font-bold tracking-wide">{title ?? 'COMPOSITE HISTORY'}</div>
        <div className="text-[#94a3b8] text-[11px] mt-1">
          Each holding&apos;s composite score now vs. 1 week / 1 month / 3 months / 6 months / 1 year ago.
          Δ shows the change vs. now (green = improving, red = deteriorating). History is forward-only —
          values are null for lookbacks before the snapshot archive started accumulating.
        </div>
      </div>
      <div>
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-[#64748b] border-b border-[#1e293b]">
          <div className="col-span-2">Symbol</div>
          <div className="col-span-1 text-right text-amber-300/80">C now</div>
          <div className="col-span-1 text-right">C 1w</div>
          <div className="col-span-1 text-right">Δ 1w</div>
          <div className="col-span-1 text-right">C 1m</div>
          <div className="col-span-1 text-right">Δ 1m</div>
          <div className="col-span-1 text-right">C 3m</div>
          <div className="col-span-1 text-right">C 6m</div>
          <div className="col-span-1 text-right">C 1y</div>
          <div className="col-span-1 text-right">Δ 1y</div>
        </div>
        {rows.map((r) => {
          const tier = compTier(r.composite);
          return (
            <div
              key={r.symbol}
              className="grid grid-cols-12 gap-2 px-4 py-1.5 text-[12px] leading-tight items-center border-b border-[#1e293b]/60 hover:bg-[#1e293b]/30 transition-colors"
            >
              <div className={`col-span-2 font-mono font-bold ${tier.color}`}>{r.symbol}</div>
              <div className={`col-span-1 text-right tabular-nums font-semibold ${tier.color}`}>
                {r.composite == null ? '—' : r.composite.toFixed(2)}
              </div>
              <div className="col-span-1 text-right">{renderHist(r.hist.w1)}</div>
              <div className="col-span-1 text-right">{renderDelta(r.composite, r.hist.w1)}</div>
              <div className="col-span-1 text-right">{renderHist(r.hist.m1)}</div>
              <div className="col-span-1 text-right">{renderDelta(r.composite, r.hist.m1)}</div>
              <div className="col-span-1 text-right">{renderHist(r.hist.m3)}</div>
              <div className="col-span-1 text-right">{renderHist(r.hist.m6)}</div>
              <div className="col-span-1 text-right">{renderHist(r.hist.y1)}</div>
              <div className="col-span-1 text-right">{renderDelta(r.composite, r.hist.y1)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
